/**
 * Foundation section utility functions for wall-foundation interactions.
 */
import { isWallFoundationInteractionOperator } from "building-generation/elements/wallAssemblies/wallAssemblyOverride";
import { getHalfEdgesFromRoomEdge } from "building-generation/generation/generateHalfEdgeAssociations";
import type { EdgeBehavior, WallAssemblyEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import { EdgeBehaviorType } from "building-types/edgeBehavior/edgeBehaviorTypesEnum";
import type { GeneratedFoundationSubSystemAny } from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallFoundationInteractionOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { WallFoundationIntersection } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { aabb2Center, aabb2XLength, aabb2YLength } from "geom/aabb2";
import { extrusionNew, faceNew, type Extrusion } from "geom/brep";
import { planeNewXY } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonIntersection } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { extrusionProfileProjectedOnPlane } from "../buildingAnalyticEntity";
import { wallCutNew } from "../wallCuts";
import { type FoundationPlanProfilesWithZRange, getFoundationPlanProfilesByZRange } from "./foundationProfiles";

// Match Studio's default typed precision for feet/inches fields (1/64").
const STUDIO_TYPED_LENGTH_PRECISION_INCHES = 1 / 64;

export interface HalfEdgeToFoundationInteractionSet {
  [halfEdge: number]: WallFoundationInteractionOperator[];
}

/** Level-keyed map of half edge to foundation interaction operators */
export type LevelHalfEdgeToFoundationInteractionSet = {
  [levelId: string]: HalfEdgeToFoundationInteractionSet;
};

export interface MakeHalfEdgeToFoundationInteractionSetParams {
  readonly edgeBehaviors: Table<EdgeBehavior>;
  readonly rooms: Table<Room>;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly roomLevelIndices: Table<number>;
  readonly levels: Table<Level>;
  readonly mirror: boolean;
}

/**
 * Builds a level-keyed map from half edge indices to wall foundation interaction operators.
 * Iterates over edge behaviors with wall assembly operators that are foundation interaction
 * operators, converts room edge IDs to half edge indices, and groups by level.
 */
export function makeHalfEdgeToFoundationInteractionSet({
  edgeBehaviors,
  rooms,
  planarGraphs,
  roomLevelIndices,
  levels,
  mirror,
}: MakeHalfEdgeToFoundationInteractionSetParams): LevelHalfEdgeToFoundationInteractionSet {
  const result: LevelHalfEdgeToFoundationInteractionSet = {};

  // Initialize empty maps for each level
  for (const levelId in levels) {
    result[levelId] = {};
  }

  // Collect wall assembly edge behaviors that have foundation interaction operators
  const foundationInteractionBehaviors: WallAssemblyEdgeBehavior[] = [];
  for (const edgeBehaviorKey of Object.keys(edgeBehaviors)) {
    const edgeBehavior = edgeBehaviors[edgeBehaviorKey];
    if (edgeBehavior.edgeBehaviorType === EdgeBehaviorType.WALL_ASSEMBLY) {
      const { wallAssemblyOperator } = edgeBehavior;
      if (wallAssemblyOperator && isWallFoundationInteractionOperator(wallAssemblyOperator)) {
        foundationInteractionBehaviors.push(edgeBehavior);
      }
    }
  }

  // Convert each edge behavior to half edge associations
  for (const edgeBehavior of foundationInteractionBehaviors) {
    const roomId = edgeBehavior.parentId;
    const room = rooms[roomId];
    if (!room) {
      continue;
    }

    const levelId = room.level.id;
    const planarGraph = planarGraphs[levelId];
    if (!planarGraph) {
      continue;
    }

    // Convert edgeId to edgeIndex
    const edgeIndex = room.edgeIds.indexOf(edgeBehavior.edgeId);
    if (edgeIndex === -1) {
      continue;
    }

    // Get all half edge indices for this room edge
    const halfEdgeIndices = getHalfEdgesFromRoomEdge(mirror, planarGraph, roomLevelIndices, roomId, edgeIndex);

    // Add the operator to each half edge
    // Safe to cast: we already verified it's a foundation interaction operator above
    const levelMap = result[levelId];
    const operator = edgeBehavior.wallAssemblyOperator as WallFoundationInteractionOperator;
    for (const halfEdgeIndex of halfEdgeIndices) {
      if (!levelMap[halfEdgeIndex]) {
        levelMap[halfEdgeIndex] = [];
      }
      levelMap[halfEdgeIndex].push(operator);
    }
  }

  return result;
}

export interface GetWallFoundationIntersectionsParams {
  readonly wallFootprint: PolyLine2;
  readonly wallZMin: number;
  readonly wallZMax: number;
  readonly foundationProfilesByZRange: FoundationPlanProfilesWithZRange;
}

/**
 * Computes 2D polygon intersections between a wall footprint and foundation
 * plan profiles, only at Z ranges where their vertical extents overlap.
 * Includes intermediate data (wallFootprint, foundationProfiles) for visualization.
 */
export function getWallFoundationIntersections({
  wallFootprint,
  wallZMin,
  wallZMax,
  foundationProfilesByZRange,
}: GetWallFoundationIntersectionsParams): WallFoundationIntersection[] {
  const result: WallFoundationIntersection[] = [];

  for (const profileData of foundationProfilesByZRange) {
    // Check vertical overlap with Studio-precision tolerance on boundary separation.
    const belowWallBy = wallZMin - profileData.zMax;
    const aboveWallBy = profileData.zMin - wallZMax;
    // Only cut if there's a greater than 1/64" overlap.
    if (belowWallBy > -STUDIO_TYPED_LENGTH_PRECISION_INCHES || aboveWallBy > -STUDIO_TYPED_LENGTH_PRECISION_INCHES) {
      continue;
    }

    // Compute 2D intersection between wall footprint and each foundation profile iteratively
    const allIntersections: PolyLine2Polygon[] = [];
    for (const profile of profileData.profiles) {
      const intersection = polygonIntersection([[wallFootprint], profile]);
      allIntersections.push(...intersection);
    }

    if (allIntersections.length > 0) {
      result.push({
        profiles: allIntersections,
        zMin: profileData.zMin,
        zMax: profileData.zMax,
      });
    }
  }

  return result;
}

export interface ComputeWallFoundationIntersectionsParams {
  readonly foundations: Table<GeneratedFoundationSubSystemAny>;
  readonly level: Level;
  readonly wallMasses: WallMassSystem;
  /** When provided, only iterate these half edges. When omitted, iterate all half edges. */
  readonly halfEdgeToFoundationInteractionSet?: HalfEdgeToFoundationInteractionSet;
}

// Takes a 3D foundation extrusion and projects it onto a wall's
// vertical plane, then derives a WallCut from the bounding box of
// the projected silhouette. The resulting WallCut is positioned in
// absolute world coordinates along the wall edge.
function createFoundationWallCut(extrusion: Extrusion, wallPlane: Plane): WallCut | undefined {
  // Project the 3D extrusion onto the wall plane, producing a 2D
  // face in wall-local coordinates
  const projectedFace = extrusionProfileProjectedOnPlane(extrusion, wallPlane);

  // A degenerate extrusion (e.g. edge-on to the wall plane) can
  // produce a face with no loops. Skip it.
  const outerLoop = projectedFace?.loops[0];
  if (!outerLoop) {
    return undefined;
  }

  const aabb = polyLine2AABB2(outerLoop);

  // Build a WallCut from the bounding box. edgeParam is the
  // horizontal center along the wall edge (absolute world coords),
  // width/height are the full extents, and bottom is the lower
  // edge elevation.
  return wallCutNew({
    edgeParam: aabb2Center(aabb)[0],
    edgeParamNormalized: false,
    edgeParamAbsolute: true,
    width: aabb2XLength(aabb),
    height: aabb2YLength(aabb),
    bottom: aabb.minY,
    opening: undefined,
  });
}

// Converts foundation-wall geometric intersections into WallCut objects
// that plug into the standard wall cut system (alongside doors, windows,
// and other openings).
//
// For each half edge that has a foundation interaction, this function:
//   1. Finds the corresponding wall mass and its plan-view footprint.
//   2. Intersects the wall footprint against every foundation system's
//      plan profiles (at each Z range where they vertically overlap).
//   3. Lifts each resulting 2D intersection polygon into a 3D extrusion
//      spanning the intersection's Z range.
//   4. Projects that extrusion onto the wall's vertical plane to get a
//      2D silhouette in wall-local coordinates.
//   5. Converts the bounding box of the silhouette into a WallCut
//      (center position along wall edge, width, height, bottom).
//
// The result is keyed by half edge index so callers can merge these
// cuts directly into halfEdgeToWallCutSet.
export function computeWallFoundationIntersections({
  foundations,
  level,
  wallMasses,
  halfEdgeToFoundationInteractionSet,
}: ComputeWallFoundationIntersectionsParams): Map<number, WallCut[]> {
  const result = new Map<number, WallCut[]>();

  // Group all foundation profiles by their vertical Z range so we can
  // test overlap against each wall's elevation span
  const foundationPlanProfilesByZRange = getFoundationPlanProfilesByZRange(foundations);

  // Vertical extent of walls on this level
  const wallZMin = level.floorElevation;
  const wallZMax = level.floorElevation + level.topOfWall;

  // When a foundation interaction set is provided, iterate only those
  // half edges. Otherwise iterate all half edges from the wall masses.
  const halfEdgeIndices: number[] = halfEdgeToFoundationInteractionSet
    ? Object.keys(halfEdgeToFoundationInteractionSet).map((s) => parseInt(s, 10))
    : Object.keys(wallMasses.halfEdgeToWall).map((s) => parseInt(s, 10));

  for (const halfEdgeIndex of halfEdgeIndices) {
    // Look up the wall mass that owns this half edge
    const wallId = wallMasses.halfEdgeToWall[halfEdgeIndex];
    const wall = wallMasses.walls[wallId];

    // Only solid mass walls can receive foundation cuts
    if (!wall || wall.wallType !== WallType.SOLID_MASS) {
      continue;
    }

    // Always key by the wall's canonical half edge index, which is
    // what makeWallSystem uses for the left-side lookup. Skip if
    // we already processed the twin half edge of this wall.
    const canonicalHalfEdge = wall.path.halfEdgeIndex;
    if (result.has(canonicalHalfEdge)) {
      continue;
    }

    const allWallCuts: WallCut[] = [];

    // The wall's left face plane is the vertical surface we'll
    // project foundation extrusions onto
    const wallPlane = wall.leftFace.plane;

    // Check every foundation system for intersections with this wall
    for (const foundationSystemId in foundationPlanProfilesByZRange) {
      // Compute 2D plan-view polygons where the wall footprint
      // overlaps the foundation profiles, filtered to Z ranges
      // that vertically overlap the wall
      const intersections = getWallFoundationIntersections({
        wallFootprint: wall.footprint,
        wallZMin,
        wallZMax,
        foundationProfilesByZRange: foundationPlanProfilesByZRange[foundationSystemId],
      });

      // Convert each intersection into WallCut objects via the
      // 3D extrusion → wall projection pipeline
      for (const intersection of intersections) {
        const height = intersection.zMax - intersection.zMin;

        // Each intersection can contain multiple disjoint 2D
        // polygons (e.g. if the foundation profile is non-convex
        // and produces separate overlap regions)
        for (const polygon of intersection.profiles) {
          // Lift the 2D polygon into a horizontal face at the
          // intersection's bottom Z, then extrude it vertically
          // to span the full intersection height
          const profile = faceNew(polygon, planeNewXY(0, 0, intersection.zMin));
          const extrusion = extrusionNew(profile, 0, 0, height);

          // Project the 3D extrusion onto the wall plane and
          // convert the bounding box into a WallCut
          const wallCut = createFoundationWallCut(extrusion, wallPlane);
          if (wallCut) {
            allWallCuts.push(wallCut);
          }
        }
      }
    }

    // Only record an entry if this wall actually produced cuts
    if (allWallCuts.length > 0) {
      result.set(canonicalHalfEdge, allWallCuts);
    }
  }

  return result;
}
